// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Tooltip,
} from '@mui/material';
import {
  KpiBoxes,
  type KpiBoxesProps,
  PageContainer,
  SectionContainer,
  Heading,
  IconButton,
  type PopupsName,
  OptionsSelector,
} from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { useTranslation } from 'react-i18next';
import { WrapperEffect } from '@advisor-app/features/ui/wrapper-effect';
import {
  type AllBenchmarks,
  formattedBenchmarks,
  selectPopularInternationalIndexes,
  selectPopularThematics,
  selectPopularUsIndexes,
} from '@advisor-app/features/utils/benchmark-formatter';
import { numberToFixed } from '@shared/ui-kit/utils';
import { useSelectUniverse } from '@advisor-app/hooks';
import {
  selectAvailableParametersEsgSectors,
  selectAvailableParametersSectors,
} from '@advisor-app/store/slices/available-parameters';
import {
  resetUniverseFilters,
  selectUniverseFilters,
  setFactors,
  updateOptimizationConfig,
  updateUniverseFilters,
} from '@advisor-app/store/slices/parameters';
import { useAppSelector } from '@advisor-app/store/store';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { type FC, type ReactNode, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useFetchUniverseData, useHandlerEquityScreener } from '../hooks';
import { EsgScreener } from './esg-screener';
import { esgSectorsFormatter } from './esg-sectors-formatter';
import { Factors } from './factors';
import { StocksTable } from './stocks-table';
import { Tickers } from './tickers';

let resetCount = 0;

interface EquityProps {
  name?: string;
  onSaveScreener?: VoidFunction;
  id: string;
}

interface EquityFilter {
  name: string;
  component: ReactNode;
  alert: PopupsName;
}

interface MarketData {
  title: string;
  placeholder: string;
  options: Array<string | { value: string; label: string }>;
  multiple: boolean;
  values: string | string[];
  onChangeValue: (value: any) => void;
  dataTestId: string;
}

export const Equity: FC<EquityProps> = ({ name, onSaveScreener, id }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

  const {
    handleExportUniverseScreener,
    isDownloadingUniverse,
    handleSaveScreener,
  } = useHandlerEquityScreener(id);

  const dispatch = useDispatch();

  const currentCurrency = useAppSelector(selectAccountCurrency);
  const sectors = useAppSelector(selectAvailableParametersSectors);
  const universe = useAppSelector(state => selectUniverseFilters(state, id))!;
  const {
    sectors_filter: sectorsFilter,
    factors_filter: factorsValuesOnRedux,
    esg_exclusions: esgSectorsFilter,
    to_exclude: tickers_exclude,
  } = universe;

  const smartBetaFactors = factorsValuesOnRedux.map(factor => factor.factor);

  const esgSectors = useAppSelector(selectAvailableParametersEsgSectors);

  const { handleSelectBenchmark, benchmarkSelected } = useSelectUniverse(id);

  let benchmarks = useAppSelector(selectPopularUsIndexes);
  benchmarks = benchmarks.concat(useAppSelector(selectPopularInternationalIndexes));
  benchmarks = benchmarks.concat(useAppSelector(selectPopularThematics));

  const parameters: EquityFilter[] = [
    {
      name: t('research.smart-beta-factors', { ns: 'research' }),
      component: (
        <Factors
          key={resetCount}
          names={smartBetaFactors}
          states={factorsValuesOnRedux}
          setFactors={factors => setFactors([id, factors])}
        />
      ),
      alert: 'screener.smartBetaFactors',
    },
    {
      name: t('research.esg-screener', { ns: 'research' }),
      component: <EsgScreener key={resetCount} id={id} />,
      alert: 'screener.esgScreener',
    },
    {
      name: t('research.include-exclude', { ns: 'research' }),
      component: (
        <div className="grid grid-cols-2 items-stretch gap-3">
          <Tickers type="Include" id={id} />
          <Tickers type="Exclude" id={id} />
        </div>
      ),
      alert: 'screener.includeExclude',
    },
  ];

  const resetFilters = () => {
    dispatch(
      updateOptimizationConfig([id, { benchmark: [{ ticker: 'SPY', weight: 1 }] }]),
    );
    dispatch(resetUniverseFilters());

    resetCount += 1;
  };

  const { summaryUniverse: customUniverse, universeData } =
    useFetchUniverseData(universe);

  const kpiData: KpiBoxesProps[] = [
    {
      text: t('research.number-of-stocks', { ns: 'research' }),
      value: (customUniverse && customUniverse.number_stocks) || 0,
      icon: 'ChartColumn',
      color: 'main',
    },
    {
      text: t('research.avg-market-cap', { ns: 'research' }),
      value:
        customUniverse &&
        new Intl.NumberFormat(locale, {
          currency: currentCurrency,
          style: 'currency',
          notation: customUniverse.market_cap >= 1_000_000 ? 'compact' : 'standard',
          compactDisplay: 'long',
          useGrouping: true,
          currencyDisplay: 'narrowSymbol',
        }).format(customUniverse.market_cap || 0),
      popupName: 'kpis.avgMarketCap',
      icon: 'DollarSign',
      color: 'main',
    },
    {
      text: t('research.price-per-earnings', { ns: 'research' }),
      value: customUniverse && numberToFixed(customUniverse.pe),
      popupName: 'kpis.pricePerEarnings',
      icon: 'Percent',
      color: 'secondary',
    },

    {
      text: t('research.dividend-yield', { ns: 'research' }),
      value: `${customUniverse && numberToFixed(customUniverse.dividend_yield)}%`,
      popupName: 'kpis.dividendYield',
      icon: 'HandCoins',
      color: 'success',
    },
  ];

  const marketData: MarketData[] = [
    {
      title: t('research.market-thematic', { ns: 'research' }),
      placeholder: t('research.select-market', { ns: 'research' }),
      options: benchmarks.map(({ ticker }) => ({
        value: ticker,
        label: `${formattedBenchmarks[ticker as AllBenchmarks]} (${ticker})`,
      })),
      multiple: false,
      values: benchmarkSelected.ticker,
      onChangeValue: handleSelectBenchmark,
      dataTestId: 'benchmark-selector',
    },
    {
      title: t('research.sector', { ns: 'research' }),
      placeholder: t('research.select-sectors', { ns: 'research' }),
      options: sectors,
      multiple: true,
      values: sectorsFilter,
      onChangeValue: values =>
        dispatch(updateUniverseFilters([id, { sectors_filter: values }])),
      dataTestId: 'sector-selector',
    },
    {
      title: t('research.esg-exclusions', { ns: 'research' }),
      placeholder: t('research.select-exclusions', { ns: 'research' }),
      options: esgSectors.map(sector => ({
        value: sector,
        label: esgSectorsFormatter(sector),
      })),
      multiple: true,
      values: esgSectorsFilter,
      onChangeValue: values =>
        dispatch(updateUniverseFilters([id, { esg_exclusions: values }])),
      dataTestId: 'esg-exclusions-selector',
    },
  ];

  const onDeletePosition = useCallback(
    (ticker: string) =>
      dispatch(
        updateUniverseFilters([id, { to_exclude: tickers_exclude.concat(ticker) }]),
      ),
    [dispatch, id, tickers_exclude],
  );

  return (
    <PageContainer
      className={`!mx-0 !px-0 md:!mx-0 2xl:!mx-0 ${name ? 'mt-8' : 'mt-[0px]'}`}
      actionBtn={
        <div
          className={`flex items-center space-x-4 ${
            name ? 'mt-[10px]' : '-mt-[35px]'
          }`}
        >
          <Button variant="outlined" color="secondary" onClick={resetFilters}>
            <Translation i18nKey="research.reset-filters" ns="research" />
          </Button>

          <Tooltip title={t('research.save-screener', { ns: 'research' })}>
            <span>
              <IconButton
                icon="Save"
                onClick={handleSaveScreener(onSaveScreener)}
              />
            </span>
          </Tooltip>

          <Tooltip title={t('research.download', { ns: 'research' })}>
            <span>
              <IconButton
                icon="Download"
                onClick={handleExportUniverseScreener}
                disabled={isDownloadingUniverse}
                data-testid="donwload-universe"
              />
            </span>
          </Tooltip>
        </div>
      }
    >
      {name && <Heading variant="h2" title={name} className="!mt-4" />}
      <section>
        <div className="!pb-4">
          <WrapperEffect showIn={!!customUniverse}>
            <div className="relative grid grid-cols-4 pt-6 transition-all duration-100 lg:gap-3 2xl:gap-6">
              {kpiData.map((vals, i) => (
                <KpiBoxes key={i} {...vals} />
              ))}
            </div>
          </WrapperEffect>
        </div>

        <div className="investment-body summary dashboard-body !relative min-h-full !overflow-visible px-0">
          <table className="!mb-4 w-full table-fixed">
            <thead>
              <tr>
                {Array.from(marketData, ({ title }, key) => (
                  <th key={key} className="py-2 pr-3 font-semibold text-title-dark">
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {Array.from(
                  marketData,
                  (
                    {
                      options,
                      multiple,
                      values,
                      onChangeValue,
                      placeholder,
                      dataTestId,
                    },
                    key,
                  ) => (
                    <td key={key} className="pr-6 last:pr-0">
                      <OptionsSelector
                        optionsAvailable={options}
                        onChangeValueSelected={onChangeValue}
                        optionSelected={values}
                        defaultText={placeholder}
                        dataTestId={dataTestId}
                        selectType="gray"
                        multiple={multiple}
                        checkbox
                        collapseSelectedLabelsAt={multiple ? 4 : undefined}
                      />
                    </td>
                  ),
                )}
              </tr>
            </tbody>
          </table>

          {parameters.map(({ name, component, alert }, i) => (
            <Accordion key={i} className="!mb-[10px] px-8 !pt-5 pb-6">
              <AccordionSummary className="!p-0">
                <Heading variant="h2" title={name} popupName={alert} tooltip />
              </AccordionSummary>
              <AccordionDetails className="!p-0">{component}</AccordionDetails>
            </Accordion>
          ))}

          <SectionContainer
            title={<Translation i18nKey="research.universe" ns="research" />}
          >
            <StocksTable data={universeData} onDeletePosition={onDeletePosition} />
          </SectionContainer>
        </div>
      </section>
    </PageContainer>
  );
};
